import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { inject } from '@angular/core';

/**
 * Shared SVG icon set, ported from the WatchGuard Studio redesign reference.
 * Stroke icons use `currentColor`; entries in FILLED render with fill instead.
 *
 *   <wg-icon name="plus" [size]="15" />
 */
const ICONS: Record<string, string> = {
  'plus': '<path d="M12 5v14M5 12h14"/>',
  'search': '<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>',
  'grid': '<rect x="4" y="4" width="7" height="7" rx="1.5"/><rect x="13" y="4" width="7" height="7" rx="1.5"/><rect x="4" y="13" width="7" height="7" rx="1.5"/><rect x="13" y="13" width="7" height="7" rx="1.5"/>',
  'check': '<path d="M5 12.5l4.5 4.5L19 7"/>',
  'check-circle': '<circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 4.5-5"/>',
  'help-circle': '<circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 1 1 5 0c0 1.5-2.5 2-2.5 3.5"/><circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none"/>',
  'chevron-right': '<path d="M9 6l6 6-6 6"/>',
  'chevron-left': '<path d="M15 6l-6 6 6 6"/>',
  'chevron-down': '<path d="M6 9l6 6 6-6"/>',
  'arrow-left': '<path d="M19 12H5m6-7l-7 7 7 7"/>',
  'arrow-up': '<path d="M12 19V5m-7 7l7-7 7 7"/>',
  'more': '<circle cx="5" cy="12" r="1.7"/><circle cx="12" cy="12" r="1.7"/><circle cx="19" cy="12" r="1.7"/>',
  'sparkle': '<path d="M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.4L12 3z"/><path d="M18.5 15l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2z"/>',
  'bot': '<rect x="5" y="8" width="14" height="11" rx="3"/><path d="M12 8V5M9.5 4.8h5"/><circle cx="9.5" cy="13" r="0.6" fill="currentColor" stroke="none"/><circle cx="14.5" cy="13" r="0.6" fill="currentColor" stroke="none"/><path d="M9.5 16.2h5"/>',
  'shield': '<path d="M12 3l7 2.8v5.4c0 4.5-3 8.2-7 9.8-4-1.6-7-5.3-7-9.8V5.8L12 3z"/>',
  'wrench': '<path d="M14.5 6.5a4 4 0 0 0-5.3 5L4 16.8a2 2 0 1 0 2.8 2.8l5.3-5.3a4 4 0 0 0 5-5.2L14.3 12 12 9.7l2.5-3.2z"/>',
  'doc': '<path d="M7 3h7l4 4v14H7V3z"/><path d="M14 3v4h4"/><path d="M10 12h5M10 16h5"/>',
  'chat': '<path d="M21 12a8 8 0 0 1-8 8c-1.2 0-2.4-.2-3.4-.7L4 21l1.7-4.6A8 8 0 1 1 21 12z"/>',
  'clock': '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/>',
  'user': '<circle cx="12" cy="8" r="4"/><path d="M4.5 20.5c1.6-3.4 4.3-5 7.5-5s5.9 1.6 7.5 5"/>',
  'users': '<circle cx="9" cy="8.5" r="3.5"/><path d="M3.5 19.5c1.3-2.9 3.2-4.3 5.5-4.3s4.2 1.4 5.5 4.3"/><path d="M15.5 5.4a3.5 3.5 0 0 1 0 6.2M17.7 15.6c1.5.7 2.4 2 2.9 3.9"/>',
  'globe': '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9S14.5 18.4 12 21c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z"/>',
  'lock': '<rect x="5.5" y="10.5" width="13" height="9.5" rx="2"/><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5"/>',
  'sun': '<circle cx="12" cy="12" r="4"/><path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5 5l1.6 1.6M17.4 17.4L19 19M19 5l-1.6 1.6M6.6 17.4L5 19"/>',
  'moon': '<path d="M20 13.5A8 8 0 0 1 10.5 4a8 8 0 1 0 9.5 9.5z"/>',
  'send': '<path d="M4 11.5L20 4l-7.5 16-1.8-6.7L4 11.5z"/>',
  'trash': '<path d="M4.5 6.5h15M9 6.5V4.8c0-.7.6-1.3 1.3-1.3h3.4c.7 0 1.3.6 1.3 1.3v1.7"/><path d="M6.5 6.5l.8 13c0 .6.6 1 1.2 1h7c.6 0 1.2-.4 1.2-1l.8-13"/>',
  'copy': '<rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4.5A1.5 1.5 0 0 1 3 13.5v-9A1.5 1.5 0 0 1 4.5 3h9A1.5 1.5 0 0 1 15 4.5V5"/>',
  'play': '<path d="M7 4.8v14.4L19.2 12 7 4.8z"/>',
  'alert': '<path d="M12 3.5l9.5 16.5h-19L12 3.5z"/><path d="M12 10v4.5"/><circle cx="12" cy="17.2" r="0.5" fill="currentColor" stroke="none"/>',
  'external': '<path d="M14 4h6v6"/><path d="M20 4l-9 9"/><path d="M19 13.5V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5.5"/>',
  'bolt': '<path d="M13 2L4.5 13.5h6L11 22l8.5-11.5h-6L13 2z"/>',
  'database': '<ellipse cx="12" cy="5.5" rx="7.5" ry="2.8"/><path d="M4.5 5.5v13c0 1.5 3.4 2.8 7.5 2.8s7.5-1.3 7.5-2.8v-13"/><path d="M4.5 12c0 1.5 3.4 2.8 7.5 2.8s7.5-1.3 7.5-2.8"/>',
  'book': '<path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15.5H6.5A2.5 2.5 0 0 0 4 21V5.5z"/><path d="M4 18.5A2.5 2.5 0 0 1 6.5 16H20"/>',
  'download': '<path d="M12 4v11m-5-5l5 5 5-5"/><path d="M4.5 20h15"/>',
  'paperclip': '<path d="M20 11.5l-8 8a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7L9 16.7a1.6 1.6 0 0 1-2.3-2.3l7.6-7.6"/>',
  'history': '<path d="M3.5 12a8.5 8.5 0 1 1 2.5 6"/><path d="M3.5 12V7.5M3.5 12H8"/><path d="M12 8v4.2l3 1.8"/>',
  'gauge': '<path d="M4.5 17.5a8.5 8.5 0 1 1 15 0"/><path d="M12 14l3.5-4.5"/><circle cx="12" cy="14.5" r="1.4"/>',
  'bell': '<path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 2h-15l1.5-2z"/><path d="M10 20.5a2 2 0 0 0 4 0"/>',
  'mail': '<rect x="3.5" y="5.5" width="17" height="13" rx="2"/><path d="M4.5 7l7.5 6 7.5-6"/>',
  'chart': '<path d="M4 4v16h16"/><path d="M8.5 15.5v-4M13 15.5V8M17.5 15.5v-6.5"/>',
  'x': '<path d="M6 6l12 12M18 6L6 18"/>',
  'edit': '<path d="M4 20h4.5L19.7 8.8a2 2 0 0 0 0-2.9l-1.6-1.6a2 2 0 0 0-2.9 0L4 15.5V20z"/>',
  'branch': '<circle cx="6" cy="5" r="2.2"/><circle cx="6" cy="19" r="2.2"/><circle cx="18" cy="9" r="2.2"/><path d="M6 7.2v9.6M18 11.2c0 3-3 4-7 4.3"/>',
  'refresh': '<path d="M20 12a8 8 0 1 1-2.3-5.6"/><path d="M20 3.5V8h-4.5"/>',
  'gear': '<circle cx="12" cy="12" r="3.2"/><path d="M12 2.8l1 2.6a7 7 0 0 1 2.4 1l2.7-.8 1.4 2.4-1.8 2.1a7 7 0 0 1 0 2.6l1.8 2.1-1.4 2.4-2.7-.8a7 7 0 0 1-2.4 1l-1 2.6h-2.8l-1-2.6a7 7 0 0 1-2.4-1l-2.7.8-1.4-2.4 1.8-2.1a7 7 0 0 1 0-2.6L3.7 8l1.4-2.4 2.7.8a7 7 0 0 1 2.4-1l1-2.6h2.8z" stroke-linejoin="round"/>',
};

/** Icons drawn with fill rather than stroke. */
const FILLED = new Set(['more', 'sparkle', 'play', 'bolt']);

@Component({
  selector: 'wg-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<svg
    [attr.width]="size()"
    [attr.height]="size()"
    viewBox="0 0 24 24"
    [attr.fill]="filled ? 'currentColor' : 'none'"
    [attr.stroke]="filled ? 'none' : 'currentColor'"
    [attr.stroke-width]="strokeWidth()"
    stroke-linecap="round"
    stroke-linejoin="round"
    [innerHTML]="content()"
  ></svg>`,
  styles: [`
    :host { display: inline-flex; flex-shrink: 0; line-height: 0; }
  `],
})
export class IconComponent {
  private readonly sanitizer = inject(DomSanitizer);

  name = input.required<string>();
  size = input<number>(16);
  strokeWidth = input<number>(1.8);

  get filled(): boolean {
    return FILLED.has(this.name());
  }

  content = computed<SafeHtml>(() =>
    this.sanitizer.bypassSecurityTrustHtml(ICONS[this.name()] ?? ''));
}
